// ============================================================
// Datei: src/pages/Register.jsx
// PURPOSE:
// Helferregistrierung
//
// ÄNDERUNGEN:
// - warning-Meldungen ergänzt
// - orange Meldung für nicht versendbare Bestätigungs-Mail
// - Scroll nach oben nach Submit bleibt erhalten
// ============================================================

import React, { useRef, useState } from "react";
import Api from "../../Api";
import "./register.css";

const Register = () => {
	const registerPageRef = useRef(null);

	const [formData, setFormData] = useState({
		firstName: "",
		lastName: "",
		birthDate: "",
		email: "",
		phoneNumber: "",
		password: ""
	});

	const [touched, setTouched] = useState({
		firstName: false,
		lastName: false,
		birthDate: false,
		email: false,
		phoneNumber: false,
		password: false
	});

	const [message, setMessage] = useState(null);
	const [submitting, setSubmitting] = useState(false);

	const scrollToTopOfForm = () => {
		if (registerPageRef.current) {
			registerPageRef.current.scrollTo({
				top: 0,
				behavior: "smooth"
			});
		}

		window.scrollTo({
			top: 0,
			behavior: "smooth"
		});
	};

	const isValidEmailSyntax = (email) =>
		/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

	const isValidPhoneNumber = (phone) => {
		if (!phone) return false;
		const normalized = phone.replace(/\s+/g, "");
		return /^(\+41|0)[0-9]{9}$/.test(normalized);
	};

	const normalizePhoneNumber = (phone) => {
		const p = phone.replace(/\s+/g, "");
		return p.startsWith("0") ? "+41" + p.substring(1) : p;
	};

	const isAtLeast18 = (birthDate) => {
		if (!birthDate) return false;

		const today = new Date();
		const birth = new Date(birthDate);

		let age = today.getFullYear() - birth.getFullYear();
		const m = today.getMonth() - birth.getMonth();

		if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;

		return age >= 18 && age <= 75;
	};

	const errors = {
		firstName:
			!formData.firstName.trim()
				? "Vorname darf nicht leer sein."
				: formData.firstName.trim().length < 2
					? "Mindestens 2 Zeichen."
					: null,

		lastName:
			!formData.lastName.trim()
				? "Nachname darf nicht leer sein."
				: formData.lastName.trim().length < 2
					? "Mindestens 2 Zeichen."
					: null,

		birthDate:
			!formData.birthDate
				? "Datum erforderlich."
				: !isAtLeast18(formData.birthDate)
					? "Du musst zwischen 18 und 75 Jahre alt sein."
					: null,

		email:
			!formData.email
				? "E-Mail erforderlich."
				: !isValidEmailSyntax(formData.email)
					? "Ungültige E-Mail."
					: null,

		phoneNumber:
			!formData.phoneNumber
				? "Telefonnummer erforderlich."
				: !isValidPhoneNumber(formData.phoneNumber)
					? "Ungültige Nummer (z. B. +41791234567)."
					: null,

		password:
			!formData.password
				? "Passwort erforderlich."
				: !/^(?=.*[0-9])(?=.*[!@#$%^&*(),.?":{}|<>]).{8,}$/.test(formData.password)
					? "Mind. 8 Zeichen, 1 Zahl, 1 Sonderzeichen."
					: null
	};

	const formValid = Object.values(errors).every((e) => e === null);

	const handleChange = (e) =>
		setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));

	const handleBlur = (field) =>
		setTouched((prev) => ({ ...prev, [field]: true }));

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!formValid || submitting) return;

		setSubmitting(true);
		setMessage(null);

		try {
			const response = await Api.post("/auth/register", {
				firstName: formData.firstName.trim(),
				lastName: formData.lastName.trim(),
				birthDate: formData.birthDate,
				password: formData.password,
				email: { address: formData.email.trim().toLowerCase() },
				phoneNumber: normalizePhoneNumber(formData.phoneNumber)
			});

			const backendStatus = response?.data?.status;
			const backendMessage =
				response?.data?.message ||
				"Wir haben dir eine E-Mail geschickt. Bitte bestätige deine E-Mail-Adresse.";

			if (backendStatus === "warning") {
				setMessage({
					type: "warning",
					text: backendMessage
				});
			} else {
				setMessage({
					type: "success",
					text: backendMessage
				});

				setFormData({
					firstName: "",
					lastName: "",
					birthDate: "",
					email: "",
					phoneNumber: "",
					password: ""
				});

				setTouched({
					firstName: false,
					lastName: false,
					birthDate: false,
					email: false,
					phoneNumber: false,
					password: false
				});
			}

			requestAnimationFrame(() => {
				scrollToTopOfForm();
			});
		} catch (err) {
			const backendStatus = err?.response?.data?.status;
			const backendError =
				err?.response?.data?.error ||
				err?.response?.data?.message ||
				(typeof err?.response?.data === "string" ? err.response.data : null) ||
				"Registrierung fehlgeschlagen.";

			setMessage({
				type: backendStatus === "warning" ? "warning" : "error",
				text: backendError
			});

			requestAnimationFrame(() => {
				scrollToTopOfForm();
			});
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<main
			ref={registerPageRef}
			className="register-page-container bg-reveal"
			data-bg={`${process.env.PUBLIC_URL}/backgrounds/background_register.png`}
		>
			<div className="register-card">
				<form onSubmit={handleSubmit} className="register-form">
					<h2 className="register-heading">Registrieren</h2>

					{message && (
						<div
							className={
								message.type === "success"
									? "register-success"
									: message.type === "warning"
										? "register-warning"
										: "register-error"
							}
						>
							{message.text}
						</div>
					)}

					<label className="register-label">Vorname</label>
					<input
						type="text"
						name="firstName"
						placeholder="Max"
						value={formData.firstName}
						onChange={handleChange}
						onBlur={() => handleBlur("firstName")}
						className={`register-input ${
							touched.firstName
								? errors.firstName ? "input-invalid" : "input-valid"
								: ""
						}`}
					/>
					{touched.firstName && errors.firstName && (
						<p className="validation-error">{errors.firstName}</p>
					)}

					<label className="register-label">Nachname</label>
					<input
						type="text"
						name="lastName"
						placeholder="Muster"
						value={formData.lastName}
						onChange={handleChange}
						onBlur={() => handleBlur("lastName")}
						className={`register-input ${
							touched.lastName
								? errors.lastName ? "input-invalid" : "input-valid"
								: ""
						}`}
					/>
					{touched.lastName && errors.lastName && (
						<p className="validation-error">{errors.lastName}</p>
					)}

					<label className="register-label">Geburtsdatum</label>
					<input
						type="date"
						name="birthDate"
						value={formData.birthDate}
						onChange={handleChange}
						onBlur={() => handleBlur("birthDate")}
						className={`register-input ${
							touched.birthDate
								? errors.birthDate ? "input-invalid" : "input-valid"
								: ""
						}`}
					/>
					{touched.birthDate && errors.birthDate && (
						<p className="validation-error">{errors.birthDate}</p>
					)}

					<label className="register-label">Telefonnummer</label>
					<input
						type="tel"
						name="phoneNumber"
						placeholder="+41 79 123 45 67"
						value={formData.phoneNumber}
						onChange={handleChange}
						onBlur={() => handleBlur("phoneNumber")}
						className={`register-input ${
							touched.phoneNumber
								? errors.phoneNumber ? "input-invalid" : "input-valid"
								: ""
						}`}
					/>
					{touched.phoneNumber && errors.phoneNumber && (
						<p className="validation-error">{errors.phoneNumber}</p>
					)}

					<label className="register-label">E-Mail</label>
					<input
						type="email"
						name="email"
						placeholder="maxmuster@festival.ch"
						value={formData.email}
						onChange={handleChange}
						onBlur={() => handleBlur("email")}
						className={`register-input ${
							touched.email
								? errors.email ? "input-invalid" : "input-valid"
								: ""
						}`}
					/>
					{touched.email && errors.email && (
						<p className="validation-error">{errors.email}</p>
					)}

					<label className="register-label">Passwort</label>
					<input
						type="password"
						name="password"
						placeholder="mind. 8 Zeichen, 1 Zahl, 1 Sonderzeichen"
						value={formData.password}
						onChange={handleChange}
						onBlur={() => handleBlur("password")}
						className={`register-input ${
							touched.password
								? errors.password ? "input-invalid" : "input-valid"
								: ""
						}`}
					/>
					{touched.password && errors.password && (
						<p className="validation-error">{errors.password}</p>
					)}

					<button
						type="submit"
						className="register-button"
						disabled={!formValid || submitting}
					>
						{submitting ? "Sende..." : "Absenden"}
					</button>
				</form>
			</div>
		</main>
	);
};

export default Register;